import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config/api";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import {
  FaPlus,
  FaArrowLeft,
  FaTrash,
  FaEdit,
  FaSchool,
  FaPhoneAlt,
  FaEnvelope,
  FaTimes,
  FaImage,
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

const imageFileAccept = ".jpg,.jpeg,.png,image/jpeg,image/png";
const imageOnlyMessage = "Only JPG and PNG images are allowed. PDF, DOC, and Excel files are not allowed.";
const isAllowedImageFile = (file) =>
  file &&
  ["image/jpeg", "image/png"].includes(file.type) &&
  /\.(jpe?g|png)$/i.test(file.name);

function AddUniform() {
  const navigate = useNavigate();
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [uniforms, setUniforms] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(null);
  const [productImages, setProductImages] = useState([]);
  const [sizeGuide, setSizeGuide] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const [expandedDesc, setExpandedDesc] = useState({});
  const [form, setForm] = useState({
    name: "",
    gender: "",
    house: "",
    description: "",
    price: "",
    sizeCategory: "",
    stock: 0,
    totalStock: 0
  });
  const BASE_URL = `${API_BASE_URL}`;

const getImageUrl = (imagePath) => {
  if (!imagePath) return "";

  const cleanPath = imagePath
    .replace(/\\/g, "/")
    .replace(/^\/+/, "");

  if (cleanPath.startsWith("http")) return cleanPath;

  if (cleanPath.startsWith("uploads/")) {
    return `${BASE_URL}/${cleanPath}`;
  }

  return `${BASE_URL}/uploads/${cleanPath}`;
};

const getPrimaryImage = (uniform) => {
  if (Array.isArray(uniform?.images) && uniform.images.length > 0) {
    return uniform.images[0];
  }

  return uniform?.image || "";
};
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
  });

  const fetchSchools = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/schools`);
      setSchools(res.data);
    } catch (err) {
      console.error("Error fetching schools", err);
    }
  };

  //dcription
  const getShortDescription = (text) => {
    if (!text) return "";
    return text.split(" ").slice(0, 3).join(" ");
  };
  const handleManualStockToggle = async (id, stock, totalStock) => {

    const isOutOfStock = stock <= 0;

    const newStock = isOutOfStock ? totalStock : 0;

    try {
      await axios.put(`${API_BASE_URL}/uniform/${id}`, {
        stock: newStock
      });

      fetchUniforms(selectedSchool._id);

    } catch (err) {
      Swal.fire("Error", "Update failed", "error");
    }

  };

  const fetchUniforms = async (schoolId) => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/uniform/by-school/${schoolId}`,
      );
      setUniforms(res.data);
    } catch (err) {
      console.error("Error fetching uniforms", err);
    }
  };

  const handleSelectSchool = (school) => {
    setSelectedSchool(school);
    setCurrentPage(1);
    fetchUniforms(school._id);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleProductImagesChange = (e) => {
    const files = Array.from(e.target.files || []);
    const invalidFile = files.find((file) => !isAllowedImageFile(file));

    if (invalidFile) {
      e.target.value = "";
      setProductImages([]);
      Swal.fire("Invalid file", imageOnlyMessage, "warning");
      return;
    }

    setProductImages(files);
  };

  const handleSizeGuideChange = (e) => {
    const file = e.target.files?.[0] || null;

    if (file && !isAllowedImageFile(file)) {
      e.target.value = "";
      setSizeGuide(null);
      Swal.fire("Invalid file", imageOnlyMessage, "warning");
      return;
    }

    setSizeGuide(file);
  };

  const openEditModal = (item) => {
    setIsEditing(item._id);
    setProductImages([]);
    setForm({
      name: item.name || "",
      gender: item.gender,
      house: item.house,
      description: item.description,
      price: item.price,
      sizeCategory: item.sizeCategory || "",
      stock: item.stock || 0,
      totalStock: item.totalStock || 0,
    });
    setShowForm(true);
  };

  const handleDeleteSchool = async (id) => {
    const result = await Swal.fire({
      title: "Delete School?",
      text: "All uniforms of this school will also be deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${BASE_URL}/schools/${id}`);

        Swal.fire("Deleted!", "School has been removed.", "success");

        setSchools((prev) => prev.filter((s) => s._id !== id));

        fetchSchools();

        if (selectedSchool && selectedSchool._id === id) {
          setSelectedSchool(null);
        }

      } catch (err) {
        console.error(err);
        Swal.fire("Error", "Delete failed", "error");
      }
    }
  };

  const toggleAvailability = async (id) => {
    try {
      await axios.put(`${API_BASE_URL}/uniform/availability/${id}`);
      fetchUniforms(selectedSchool._id);
      Swal.fire({ icon: "success", title: "Availability updated" });
    } catch (err) {
      Swal.fire("Error", "Failed to update availability", "error");
    }
  };
  const handleDelete = async (id) => {

    const result = await Swal.fire({
      title: "Delete Uniform?",
      text: "This item will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!"
    });

    if (result.isConfirmed) {
      try {

        await axios.delete(`${API_BASE_URL}/uniform/${id}`);

        Swal.fire("Deleted!", "Uniform removed from inventory.", "success");

        fetchUniforms(selectedSchool._id);

      } catch (err) {
        console.error(err);
        Swal.fire("Error", "Delete failed", "error");
      }
    }
  };
  const closeModal = () => {
    setShowForm(false);
    setIsEditing(null);
    setForm({
      name: "",
      gender: "",
      house: "",
      description: "",
      price: "",
      sizeCategory: "",
      stock: 0,
      totalStock: 0,
    });
    setProductImages([]);
    setSizeGuide(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.gender) {
      return Swal.fire("Wait!", "Please select Gender", "info");
    }

    const formData = new FormData();
    formData.append("schoolId", selectedSchool._id);
    Object.keys(form).forEach((key) => formData.append(key, form[key]));
    productImages.forEach((file) => formData.append("image", file));
    if (sizeGuide) formData.append("sizeGuide", sizeGuide);

    try {
      if (isEditing) {
        await axios.put(`${API_BASE_URL}/uniform/${isEditing}`, formData);
        Swal.fire({ icon: "success", title: "Updated successfully" });
      } else {
        await axios.post(`${API_BASE_URL}/uniform`, formData);
        Swal.fire("Success", "New uniform added to inventory!", "success");
      }
      fetchUniforms(selectedSchool._id);
      closeModal();
    } catch (err) {
      Swal.fire(
        "Error saving item",
        err.response?.data?.message || "Check Server Connection",
        "error",
      );
    }
  };

  useEffect(() => {
    fetchSchools();
  }, []);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentUniforms = uniforms.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(uniforms.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="p-4" style={{ minHeight: "100vh", background: "#f8f9fa" }}>
      {!selectedSchool ? (
        <div
          className="card border-0 shadow-sm mx-auto"
          style={{ maxWidth: "1000px", borderRadius: "20px" }}
        >
          <div
            className="card-header bg-white fw-bold py-3 border-0"
            style={{ fontSize: "1.2rem" }}
          >
            Registered Schools
          </div>
          <div className="list-group list-group-flush">
            {schools.map((s) => (
              <div
                key={s._id}
                className="list-group-item list-group-item-action d-flex justify-content-between align-items-center p-4 border-light"
                style={{ cursor: "pointer" }}
                onClick={() => handleSelectSchool(s)}
              >
                <div className="d-flex align-items-center gap-3">
                  <div className="profile-img-placeholder">
                    <FaSchool className="text-success" />
                  </div>
                  <div>
                    <span className="fw-bold text-dark d-block">{s.name}</span>
                    <span className="text-muted x-small">
                      Code: {s.schoolCode}
                    </span>
                  </div>
                </div>
                <button className="btn btn-light rounded-pill px-4 btn-sm fw-bold text-success border">
                  View
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="mx-auto" style={{ maxWidth: "1200px" }}>
          <button
            className="btn btn-md d-flex align-items-center gap-2 fw-bold text-muted border-0 bg-transparent"
            onClick={() => navigate("/dashboard")}><FaArrowLeft /> Back</button>

          <div className="row g-4">
            <div className="col-lg-5">
              <div className="card border-0 shadow-sm p-4 h-100 rounded-4 bg-white">
                <div className="d-flex align-items-center gap-3 mb-4">
                  <div className="p-3 bg-light rounded-circle">
                    <FaSchool size={30} className="text-success" />
                  </div>
                  <div>
                    <h4 className="fw-bold text-success mb-0">
                      {selectedSchool.name}
                    </h4>
                    <small className="text-muted">
                      Code: {selectedSchool.schoolCode}
                    </small>
                  </div>
                </div>

                <button
                  className="btn w-100 mt-3 py-2 fw-bold btn btn school-btn"
                  style={{ borderRadius: "10px" }}
                  onClick={() => setShowForm(true)}
                >
                  <FaPlus className="me-2" /> Add Uniform
                </button>
              </div>
            </div>

            <div className="col-lg-7">
              <div className="card border-0 shadow-sm p-4 h-100 rounded-4 bg-white">
                <div className="row g-4">
                  <div className="col-md-4">
                    <div className="info-item text-center p-2 border rounded">
                      <FaCalendarAlt className="text-primary mb-2" />
                      <p className="text-muted mb-0 x-small">Active Items</p>
                      <h6 className="fw-bold">{uniforms.length}</h6>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="info-item text-center p-2 border rounded">
                      <FaPhoneAlt className="text-primary mb-2" />
                      <p className="text-muted mb-0 x-small">Phone</p>
                      <h6 className="fw-bold" style={{ fontSize: "12px" }}>
                        {selectedSchool.phone || "N/A"}
                      </h6>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="info-item text-center p-2 border rounded">
                      <FaEnvelope className="text-primary mb-2" />
                      <p className="text-muted mb-0 x-small">Email</p>
                      <h6 className="fw-bold" style={{ fontSize: "12px" }}>
                        {selectedSchool.email}
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-5">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="fw-bold d-flex align-items-center gap-2 m-0">
                <div
                  style={{
                    width: "4px",
                    height: "20px",
                    background: "#10b981",
                    borderRadius: "10px",
                  }}
                ></div> Uniform List</h5>
              {totalPages > 1 && (
                <div className="d-flex align-items-center gap-2">
                  <button
                    className="btn btn-sm btn-white border shadow-sm rounded-circle p-2"
                    disabled={currentPage === 1}
                    onClick={() => paginate(currentPage - 1)}
                  >
                    <FaChevronLeft size={10} />
                  </button>
                  <span className="x-small fw-bold">
                    Page {currentPage} / {totalPages}
                  </span>
                  <button
                    className="btn btn-sm btn-white border shadow-sm rounded-circle p-2"
                    disabled={currentPage === totalPages}
                    onClick={() => paginate(currentPage + 1)}
                  >
                    <FaChevronRight size={10} />
                  </button>
                </div>
              )}
            </div>

<div className="row g-4">
  {currentUniforms.length > 0 ? (
    currentUniforms.map((u) => {
      const primaryImage = getPrimaryImage(u);
      const imageCount =
        Array.isArray(u.images) && u.images.length > 0 ? u.images.length : primaryImage ? 1 : 0;

      return (
      <div className="col-md-6" key={u._id}>
        <div
          className={`card border-0 rounded-4 overflow-hidden h-100 ${u.stock <= 0 ? "opacity-75" : ""}`}
          style={{ 
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            transition: 'all 0.3s ease',
            border: '1px solid #f0f0f0'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div className="card-body p-3">
            <div className="d-flex gap-3">
              {/* Image Section */}
              <div className="position-relative flex-shrink-0">
                <img
  src={getImageUrl(primaryImage)}
  alt="Product"
  className="rounded-3 border-0 shadow-sm"
  style={{ width: 110, height: 110, objectFit: "cover", background: "#f8f9fa" }}
  onError={(e) => {
    console.log("Image failed:", primaryImage, getImageUrl(primaryImage));
    e.target.src = "https://via.placeholder.com/110x110?text=No+Image";
  }}
/>
                {imageCount > 1 && (
                  <span className="badge bg-dark text-white position-absolute bottom-0 end-0 m-1 shadow-sm border border-white" style={{ fontSize: '0.65rem', fontWeight: '800' }}>
                    {imageCount} IMAGES
                  </span>
                )}
                {u.stock <= 10 && u.stock > 0 && (
                  <span className="badge bg-warning text-dark position-absolute top-0 start-0 m-1 shadow-sm border border-white" style={{ fontSize: '0.65rem', fontWeight: '800' }}>
                    LOW STOCK
                  </span>
                )}
              </div>

              {/* Main Info Section */}
              <div className="flex-grow-1">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: "1rem", letterSpacing: '-0.3px' }}>
                      {u.name}
                    </h6>
                    <span className="text-primary fw-semibold text-uppercase" style={{ fontSize: "0.7rem", letterSpacing: '0.5px' }}>
                      {u.house} House
                    </span>
                  </div>
                  <div className="d-flex gap-1">
                    <button onClick={() => openEditModal(u)} className="btn btn-light btn-sm rounded-circle text-muted">
                      <FaEdit size={20} />
                    </button>
                    <button onClick={() => handleDelete(u._id)} className="btn btn-light btn-sm rounded-circle text-danger">
                      <FaTrash size={20} />
                    </button>
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2 mt-2">
                  <span className="badge bg-dark text-white rounded-pill px-3 py-1" style={{ fontSize: '0.8rem' }}>
                    ₹{u.price}
                  </span>
                  <span className="text-muted border-start ps-2" style={{ fontSize: "0.75rem" }}>
                    {u.gender}
                  </span>
                </div>

                <div className="mt-3">
                  <div 
                    className="p-2 rounded-3 bg-light-subtle border" 
                    onClick={() => setExpandedDesc((prev) => ({ ...prev, [u._id]: !prev[u._id] }))}
                    style={{ cursor: 'pointer' }}
                  >
                    <p className="mb-0 text-muted" style={{ fontSize: "0.75rem", lineHeight: '1.4' }}>
                      <b className="text-dark">Info:</b>{" "}
                      {expandedDesc[u._id] ? u.description : `${getShortDescription(u.description)}...`}
                      {u.description && u.description.split(" ").length > 3 && (
                        <span className="text-success ms-1 fw-bold">
                          {expandedDesc[u._id] ? " Show Less" : " Read More"}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Stock Bar */}
            <div className="mt-3 pt-3 border-top d-flex align-items-center justify-content-between">
              <div>
                <span className={`fw-bold d-block ${u.stock > 0 ? "text-success" : "text-danger"}`} style={{ fontSize: '0.7rem' }}>
                   ● {u.stock > 0 ? "IN STOCK" : "OUT OF STOCK"}
                </span>
                <small className="text-muted" style={{ fontSize: '0.65rem' }}>Current: {u.stock} / {u.totalStock} units</small>
              </div>

              <div
                className="stock-toggle-switch shadow-sm"
                onClick={() => handleManualStockToggle(u._id, u.stock, u.totalStock)}
                style={{
                  width: '46px',
                  height: '24px',
                  backgroundColor: u.stock > 0 ? '#10b981' : '#cbd5e1',
                  borderRadius: '20px',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease'
                }}
              >
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    position: 'absolute',
                    top: '2px',
                    left: u.stock > 0 ? '24px' : '2px',
                    transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      );
    })
  ) : (
    <div className="col-12 text-center py-5">
       <div className="display-6 mb-2">📦</div>
       <h5 className="text-muted fw-light">No uniforms found in inventory.</h5>
    </div>
  )}
</div>
          </div>
        </div>
      )}

      {/* MODAL FORM */}
      <AnimatePresence>
        {showForm && (
          <div className="custom-modal-overlay">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="custom-modal-card"
            >
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="fw-bold m-0 text-dark">
                  {isEditing ? "Update Uniform" : "Add Uniform Item"}
                </h5>
                <button
                  className="btn btn-light rounded-circle p-2"
                  onClick={closeModal}
                >
                  <FaTimes />
                </button>
              </div>
             <form onSubmit={handleSubmit} className="row g-4">

      {/* BASIC INFO */}
      <div className="col-12">
        <h6 className="fw-bold text-secondary border-bottom pb-2">
          Basic Information
        </h6>
      </div>

      {/* Uniform Name */}
      <div className="col-md-6">
        <label className="form-label fw-semibold">Uniform Title</label>
        <input
          name="name"
          value={form.name}
          onChange={handleChange}
          placeholder="DPS Summer Shirt"
          className="form-control shadow-sm"
          style={{ borderRadius: "10px" }}
          required
        />
      </div>

      {/* House */}
      <div className="col-md-6">
        <label className="form-label fw-semibold">House</label>
        <input
          name="house"
          value={form.house}
          onChange={handleChange}
          placeholder="Red House"
          className="form-control shadow-sm"
          style={{ borderRadius: "10px" }}
          required
        />
      </div>

      <div className="col-md-6">
        <label className="form-label fw-semibold">Category</label>
        <input
          type="text"
          name="sizeCategory"
          value={form.sizeCategory}
          onChange={handleChange}
          placeholder="e.g. Pant, Tie, Shirt"
          className="form-control shadow-sm"
          style={{ borderRadius: "10px" }}
          required
        />
      </div>
      {/* Gender */}
      <div className="col-md-6">
        <label className="form-label fw-semibold">Gender</label>

        <select
          name="gender"
          value={form.gender}
          onChange={handleChange}
          className="form-select shadow-sm"
          style={{ borderRadius: "10px" }}
        >
          <option value="">Select Gender</option>
          <option>Male</option>
          <option>Female</option>
        </select>
      </div>

      {/* STOCK SECTION */}
      <div className="col-12 mt-3">
        <h6 className="fw-bold text-secondary border-bottom pb-2">
          Pricing & Stock
        </h6>
      </div>

      {/* Price */}
      <div className="col-md-4">
        <label className="form-label fw-semibold">Price (₹)</label>

        <div className="input-group shadow-sm">
          <span className="input-group-text">₹</span>
          <input
            name="price"
            value={form.price}
            type="number"
            className="form-control"
            onChange={handleChange}
            required
          />
        </div>
      </div>

      {/* Stock */}
      <div className="col-md-4">
        <label className="form-label fw-semibold">Stock Quantity</label>

        <input
          name="stock"
          type="number"
          value={form.stock}
          onChange={handleChange}
          className="form-control shadow-sm no-number-spinner"
          style={{ borderRadius: "10px" }}
        />
      </div>

      {/* Total Stock */}
      <div className="col-md-4">
        <label className="form-label fw-semibold">Total Stock</label>

        <input
          name="totalStock"
          type="number"
          value={form.totalStock}
          onChange={handleChange}
          className="form-control shadow-sm no-number-spinner"
          style={{ borderRadius: "10px" }}
        />
      </div>

      {/* DESCRIPTION */}
      <div className="col-12">
        <label className="form-label fw-semibold">Description</label>

        <textarea
          name="description"
          value={form.description}
          rows="3"
          onChange={handleChange}
          placeholder="Fabric, washing instructions etc."
          className="form-control shadow-sm"
          style={{ borderRadius: "12px" }}
        />
      </div>

      {/* IMAGE UPLOAD */}
      <div className="col-md-6">
        <label className="form-label fw-semibold">
          <FaImage className="me-2 text-success" />
          Product Images
        </label>

        <input
          type="file"
          className="form-control shadow-sm"
          accept={imageFileAccept}
          multiple
          onChange={handleProductImagesChange}
        />
        <small className="text-muted d-block mt-2">
          {productImages.length > 0
            ? `${productImages.length} image${productImages.length > 1 ? "s" : ""} selected`
            : isEditing
              ? "Select new images only if you want to replace the current product images."
              : "You can select multiple product photos."}
        </small>
      </div>

      {/* SIZE GUIDE */}
      <div className="col-md-6">
        <label className="form-label fw-semibold">
          <FaImage className="me-2 text-primary" />
          Size Guide Chart
        </label>

        <input
          type="file"
          className="form-control shadow-sm"
          accept={imageFileAccept}
          onChange={handleSizeGuideChange}
        />
      </div>

      {/* BUTTON */}
      <div className="col-12 mt-3">
        <button
          type="submit"
          className="btn w-100 text-white fw-bold py-3 shadow"
          style={{
            borderRadius: "12px",
            background:
              "#097969",
          }}
        >
          {isEditing
            ? "🚀 Update Uniform"
            : " Save Uniform"}
        </button>
      </div>
    </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      <style>{`
        .custom-modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0, 0, 0, 0.4); backdrop-filter: blur(8px); display: flex; justify-content: center; align-items: center; z-index: 9999; }
        .custom-modal-card { background: white; padding: 30px; border-radius: 25px; width: 95%; max-width: 600px; box-shadow: 0 20px 40px rgba(0,0,0,0.1); max-height: 90vh; overflow-y: auto; }
        .x-small { font-size: 11px; }
        .profile-img-placeholder { width: 50px; height: 50px; background: #e8f5e9; border-radius: 12px; display: flex; align-items: center; justify-content: center; }
        .profile-circle { width: 70px; height: 70px; background: #f8f9fa; border: 2px solid #eee; border-radius: 50%; display: flex; align-items: center; justify-content: center; }
        // .hover-up:hover { transform: translateY(-5px); transition: 0.3s; box-shadow: 0 15px 30px rgba(0,0,0,0.08) !important; }
        .cursor-pointer { cursor: pointer; }
      `}</style>
    </div>
  );
}
export default AddUniform;
